import * as t from 'io-ts';
import {chain, filter, map} from 'lodash';

import {colorForInteger} from '../../../libs/shared/ui/src/styles/colorHelpers';
import {PaletteColorsEnum} from '../../../libs/shared/ui/src/styles/paletteModel';
import {IntegrationTypeCategoriesEnum, parseIntegrationTypesCategories} from '../api/integrationTypesModel';
import {makeApiType, makeEnumRuntimeType} from './apiTypes';
import {RApplicationEntryPointActionFeaturesEnum} from './applicationEntryPointActionModel';
import {parseApplicationPreferences, RApplicationPreferences} from './applicationPreferencesModel';
import {parseLegacyEntryPoint, RLegacyEntryPoint} from './legacyEntryPointModel';

/*
 * Runtime type.
 */

const RLegacyApplicationDirty = t.readonly(
  t.intersection([
    t.type({
      id: t.Integer,
      typeId: t.string,
      name: t.string,
      color: makeEnumRuntimeType(PaletteColorsEnum),
      bundleUrl: t.string,
      features: t.readonlyArray(RApplicationEntryPointActionFeaturesEnum),
      entryPoints: t.readonlyArray(RLegacyEntryPoint),
      isEnabled: t.boolean,
      updatedAt: t.Integer,
      namespacePreferences: t.dictionary(t.string, t.union([t.undefined, RApplicationPreferences])),
      url: t.string,
    }),
    t.partial({
      iconUrl: t.string,
      baseUrl: t.string,
      authenticationUrl: t.string,
      categories: t.readonlyArray(makeEnumRuntimeType(IntegrationTypeCategoriesEnum)),
    }),
  ]),
  'application',
);

/*
 * Type.
 */

export interface LegacyApplicationModel extends t.TypeOf<typeof RLegacyApplicationDirty> {}
export const RLegacyApplication = t.clean<LegacyApplicationModel>(RLegacyApplicationDirty as any);

/*
 * IO methods.
 */

export function parseLegacyApplication(src: any): LegacyApplicationModel {
  return {
    id: src.id,
    typeId: src.type_id,
    name: src.name,
    iconUrl: src.icon_url,
    color: colorForInteger(src.id),
    categories: parseIntegrationTypesCategories(src.categories || []),
    authenticationUrl: src.authentication_url,
    baseUrl: src.api_base_url,
    bundleUrl: (src.bundle_urls && src.bundle_urls.web) || '',
    features: filter(src.features, RApplicationEntryPointActionFeaturesEnum.is),
    entryPoints: map(src.entry_points, (ep, id) => parseLegacyEntryPoint({id, ...ep})),
    isEnabled: src.enabled,
    updatedAt: src.updated_at,
    namespacePreferences: chain(src.installations)
      .map(parseApplicationPreferences)
      .keyBy((s) => s.namespace)
      .value(),
    url: src.url,
  };
}

/*
 * API type.
 */

export const ApiLegacyApplication = makeApiType('application', RLegacyApplication, parseLegacyApplication);
